import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';
import { CWTSchema, CWTSchemaField, CWTSchemaPropertySummary, FieldType } from '../../../../../types/custom-worksheet-v2';
import { useDispatch } from 'react-redux';
import ToggleField from '../ToggleField';
import DefaultDescription from './DefaultDescription';
import DefaultDate from './DefaultDate';
import PositiveNature from './PositiveNature';
import { isCWTSchemaTable, isInCollection } from '../../../store/utils';
import { Description } from '../Configurations';
import { addQuickAction, removeQuickAction } from '../../../store/slice';
import { NatureEnum } from '../../../../../types/nature';

const ALLOWED_TYPES = [FieldType.MONEY];

type ReconciliationProps = {
    property: CWTSchemaField | CWTSchemaPropertySummary;
    schema: CWTSchema;
};

function Reconciliation({ property, schema }: ReconciliationProps): JSX.Element | null {
    const dispatch = useDispatch();

    const collection = isInCollection(schema, property);
    const propertyId = property.id.split('.').at(-1) as string;
    const summary = collection?.summary?.properties?.[propertyId];
    // If the field is a table column, it must be summarised before configuration.
    // We only want reconciliation entries for summarised columns.
    const requireSummary = collection && isCWTSchemaTable(collection) && (collection.summary === null || !summary);

    const useSummary = collection && isCWTSchemaTable(collection);

    const [isCollapsed, setIsCollapsed] = useState(false);

    // Is this field a type that is allowed to have reconciliation entries?
    const canConfigure = ALLOWED_TYPES.includes(property.type);

    if (!canConfigure) {
        return null;
    }

    const field = useSummary ? summary : property;

    if (!field) {
        return <></>;
    }

    /**
     * On toggle
     * - If property currently has reconciliation entry toggled, remove it
     * - Else set reconciliation object with default DEBIT nature
     */
    function handleToggleReconciliation(field: CWTSchemaField | CWTSchemaPropertySummary): void {
        if (field.quickActions?.find((qa) => qa?.type === 'CREATE_ENTRY')) {
            // Remove it
            dispatch(removeQuickAction({ prop: field, type: 'CREATE_ENTRY' }));
        } else {
            // Add it
            dispatch(
                addQuickAction({
                    prop: field,
                    action: {
                        type: 'CREATE_ENTRY',
                        payload: {
                            defaultDate: null,
                            defaultDescription: null,
                            positiveNature: NatureEnum.DEBIT,
                        },
                    },
                })
            );
        }
    }

    const createEntryAction = field.quickActions?.find((qa) => qa.type === 'CREATE_ENTRY');

    return (
        <div className="flex flex-col">
            <div className="flex items-center pl-4 pt-4">
                <ChevronDownIcon
                    className={`hover:cursor-pointer w-4 h-4 -ml-4 text-gray-700 transition-transform ${isCollapsed ? '-rotate-90' : ''}`}
                    onClick={(): void => setIsCollapsed(!isCollapsed)}
                />
                <p className="text-sm font-bold leading-none text-gray-700">Reconciliation</p>
            </div>

            <AnimatePresence initial={false}>
                {!isCollapsed && (
                    <motion.div
                        exit={{ height: 0, opacity: 0 }}
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        transition={{ height: { duration: 0.2 } }}
                        className="overflow-hidden"
                    >
                        {requireSummary ? (
                            <div className="mt-4 flex flex-col gap-4 pl-4 pb-4 pr-0.5">
                                <Description
                                    title={'Reconciliation Entry'}
                                    description={'This column must be summarised before it can make a reconciliation entry.'}
                                />
                            </div>
                        ) : (
                            <div className="mt-4 flex flex-col gap-4 pl-4 pb-4 pr-0.5">
                                {/* ALLOW FIELD TO CREATE ENTRY */}
                                <ToggleField
                                    title="Reconciliation Entry"
                                    description="Allow this field to create a reconciliation entry."
                                    defaultEnabled={Boolean(createEntryAction)}
                                    onToggle={(): void => handleToggleReconciliation(field)}
                                />
                                {createEntryAction && (
                                    <>
                                        <DefaultDescription id={property.id} quickAction={createEntryAction} isSummary={Boolean(summary)} />
                                        <DefaultDate id={property.id} quickAction={createEntryAction} isSummary={Boolean(summary)} />
                                        <PositiveNature id={property.id} quickAction={createEntryAction} isSummary={Boolean(summary)} />
                                    </>
                                )}
                            </div>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

export default Reconciliation;
